import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import HighlightMark from '../../../MyComponents/LandingSections/HighlightMark';
import LandingSection from '../../../MyComponents/LandingSections/LandingSection';
import { START_FREE_LINK } from '../../../common/urlLinks';
import useStartCta from '../../../MyComponents/LandingSections/useStartCta';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

const useStyles = makeStyles(() => ({
    card: {
        boxSizing: 'border-box',
        position: 'relative',
        // Clips the two decorative shapes that bleed off opposite corners.
        overflow: 'hidden',
        padding: '72px 40px',
        borderRadius: '30px',
        textAlign: 'center',
        background: REDESIGN_COLORS.highlight,
    },
    ring: {
        position: 'absolute',
        width: '220px',
        height: '220px',
        left: '-120px',
        top: '-100px',
        border: `42px solid ${REDESIGN_COLORS.accent}`,
        borderRadius: '50%',
        pointerEvents: 'none',
    },
    disc: {
        position: 'absolute',
        width: '180px',
        height: '180px',
        right: '-80px',
        bottom: '-90px',
        borderRadius: '50%',
        background: REDESIGN_COLORS.primary,
        pointerEvents: 'none',
    },
    content: {
        position: 'relative',
        zIndex: 2,
    },
    title: {
        margin: '0 auto 17px',
        maxWidth: '780px',
        fontSize: 'clamp(2rem, 4.2vw, 3.4rem)',
        lineHeight: 1.08,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    lead: {
        margin: '0 auto 28px',
        maxWidth: '620px',
        color: REDESIGN_COLORS.body,
    },
    cta: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '52px',
        padding: '0 26px',
        borderRadius: '999px',
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.dark,
        boxShadow: '0 4px 12px rgba(2, 46, 50, 0.24)',
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '1rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        '&:hover': {
            color: REDESIGN_COLORS.white,
            background: REDESIGN_COLORS.darkHover,
        },
    },
    points: {
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '10px',
        margin: '20px 0 0',
        padding: 0,
        listStyle: 'none',
        color: REDESIGN_COLORS.body,
        fontSize: '0.85rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
}));

/**
 * The `#contact` closing card: amber panel, white marker on the wordmark, and
 * the one call to action the page ends on.
 */
const HomeContact = ({ keyPrefix = 'redesign.home.contact', to = START_FREE_LINK }) => {
    const classes = useStyles();
    // Signed in, "start for free" becomes "see the plans".
    const cta = useStartCta(to);

    return (
        <LandingSection id="contact" background="none" paddingTop={26} paddingBottom={78}>
            <div className={classes.card}>
                <div aria-hidden="true" className={classes.ring} />
                <div aria-hidden="true" className={classes.disc} />

                <div className={classes.content}>
                    <h2 className={classes.title}>
                        <FormattedMessage
                            id={`${keyPrefix}.title`}
                            values={{
                                mark: (chunks) => <HighlightMark variant="light">{chunks}</HighlightMark>,
                            }}
                        />
                    </h2>
                    <p className={classes.lead}>
                        <FormattedMessage id={`${keyPrefix}.lead`} />
                    </p>
                    <Link to={cta.to} className={classes.cta}>
                        <FormattedMessage id={cta.labelId || `${keyPrefix}.cta`} />
                    </Link>
                    <ul className={classes.points}>
                        <li>
                            <FormattedMessage id={`${keyPrefix}.point1`} />
                        </li>
                        <li aria-hidden="true">·</li>
                        <li>
                            <FormattedMessage id={`${keyPrefix}.point2`} />
                        </li>
                    </ul>
                </div>
            </div>
        </LandingSection>
    );
};

export default HomeContact;
